import { CircularProgress, Box } from '@mui/material';
import React from 'react';

const PULL_START_THRESHOLD = 4;
const PULL_END_THRESHOLD = 128;

/**
 * Plocha s vlastným zvislým gestom - spodný list, mapa. Ťah, ktorý na nej
 * začne, nie je nikdy ťahom na obnovenie: zatvorenie listu ťahom nadol by
 * inak načítalo celú stránku.
 */
const BLOCKED_SELECTOR = '[data-pulldown-blocked="true"]';

/**
 * Je niektorý kontajner medzi miestom dotyku a obalom (vrátane neho)
 * odrolovaný? Taký ťah nadol patrí jemu - skroluje späť na začiatok. Prvok,
 * ktorý neskroluje, má `scrollTop` vždy 0, takže netreba zisťovať `overflow`.
 */
const isScrolledBetween = (element: Element | null, root: HTMLElement) => {
  for (let node = element; node; node = node.parentElement) {
    if (node.scrollTop > 0) return true;
    if (node === root) return false;
  }
  return false;
};

type Gesture = {
  x: number;
  y: number;
  /** smer sa už potvrdil ako ťah nadol */
  pulling: boolean;
};

type Props = {
  target: React.RefObject<HTMLElement>;
  desktop?: boolean;
  onPull: () => Promise<void>;
};

/**
 * Obnovenie stránky ťahom nadol na mobile. Natívne obnovenie prehliadača je
 * vypnuté (`overscroll-behavior` v `App.tsx`), aby sa tieto dve nebili.
 *
 * O tom, či ťah vôbec môže byť obnovením, sa rozhoduje **na jeho začiatku**:
 * stránka aj všetko pod prstom musí byť odrolované hore. Keby sa to
 * vyhodnocovalo počas pohybu, švih nahor, ktorý v tom istom ťahu dorazí na
 * začiatok stránky, by sa prehupol do obnovenia. Smer sa potvrdí až po
 * `PULL_START_THRESHOLD` - ťah do strany (galéria, široká tabuľka) a dva prsty
 * gesto zrušia.
 */
const Pulldown: React.FC<Props> = ({ target, desktop, onPull }) => {
  const gesture = React.useRef<Gesture | null>(null);
  const [distance, setDistance] = React.useState(0);
  const [pulling, setPulling] = React.useState(false);
  const [pulled, setPulled] = React.useState(false);

  const reset = React.useCallback(() => {
    gesture.current = null;
    setPulling(false);
    setDistance(0);
  }, []);

  const onStart = React.useCallback(
    (event: TouchEvent | MouseEvent) => {
      if (pulled) return;
      reset();

      const root = target.current;
      const element = event.target as Element | null;
      if (
        !root ||
        ('touches' in event && event.touches.length > 1) ||
        element?.closest(BLOCKED_SELECTOR) ||
        isScrolledBetween(element, root)
      ) {
        return;
      }

      const point = 'touches' in event ? event.touches[0] : event;
      gesture.current = { x: point.clientX, y: point.clientY, pulling: false };
    },
    [pulled, reset, target],
  );

  const onMove = React.useCallback(
    (event: TouchEvent | MouseEvent) => {
      const current = gesture.current;
      if (!current) return;
      if ('touches' in event && event.touches.length > 1) {
        reset();
        return;
      }

      const point = 'touches' in event ? event.touches[0] : event;
      const dx = point.clientX - current.x;
      const dy = point.clientY - current.y;

      if (!current.pulling) {
        if (
          Math.abs(dx) <= PULL_START_THRESHOLD &&
          Math.abs(dy) <= PULL_START_THRESHOLD
        ) {
          // Smer ešte nie je jasný. Ťah nadol sa zadrží, inak by si ho
          // prehliadač vzal ako skrolovanie a ďalšie pohyby by sa už nedali
          // zrušiť.
          if (dy > 0) event.preventDefault();
          return;
        }
        // Nahor alebo do strany - patrí to obsahu, nie obnoveniu.
        if (Math.abs(dx) >= dy) {
          reset();
          return;
        }
        current.pulling = true;
        setPulling(true);
      }

      event.preventDefault();
      setDistance(dy);
      // Ťah späť nad východisko posunie východisko, nad prah ho drží na prahu.
      if (dy < 0) current.y = point.clientY;
      if (dy > PULL_END_THRESHOLD) {
        current.y = point.clientY - PULL_END_THRESHOLD;
      }
    },
    [reset],
  );

  const onEnd = React.useCallback(async () => {
    gesture.current = null;
    setPulling(false);
    setDistance(0);
    if (distance >= PULL_END_THRESHOLD && !pulled) {
      setPulled(true);
      await onPull();
      setPulled(false);
    }
  }, [distance, pulled, onPull]);

  React.useEffect(() => {
    const { current: t } = target;
    if (t) {
      t.addEventListener('touchstart', onStart, { passive: false });
      t.addEventListener('touchmove', onMove, { passive: false });
      t.addEventListener('touchend', onEnd);
      if (desktop) {
        t.addEventListener('mousedown', onStart, { passive: false });
        t.addEventListener('mousemove', onMove, { passive: false });
        t.addEventListener('mouseup', onEnd);
      }
    }

    return () => {
      const { current: t } = target;
      if (t) {
        t.removeEventListener('touchstart', onStart);
        t.removeEventListener('touchmove', onMove);
        t.removeEventListener('touchend', onEnd);
        if (desktop) {
          t.removeEventListener('mousedown', onStart);
          t.removeEventListener('mousemove', onMove);
          t.removeEventListener('mouseup', onEnd);
        }
      }
    };
  }, [target, desktop, onStart, onMove, onEnd]);

  const progress = Math.max(
    0,
    Math.min(100, (distance / PULL_END_THRESHOLD) * 100),
  );

  return (
    <Box
      sx={({ typography, spacing, shadows }) => ({
        position: 'absolute',
        top: typography.pxToRem(24),
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        padding: spacing(1),
        // Just under modal z-index
        zIndex: 1299,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '100%',
        boxShadow: shadows[3],
        visibility: 'hidden',
        opacity: 0,
        '&.visible': {
          visibility: 'visible',
          opacity: 1,
        },
        transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
      })}
      className={pulling || pulled ? 'visible' : ''}
    >
      <CircularProgress
        size={24}
        thickness={5}
        variant={pulled ? 'indeterminate' : 'determinate'}
        value={progress}
        sx={{
          '.MuiCircularProgress-circle': pulling ? { transition: 'none' } : {},
        }}
      />
    </Box>
  );
};

export default Pulldown;
